import React, { useEffect, useRef } from 'react';
import { NavLink, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AccountSettings from './account/AccountSettings';
import FamilyOverview from './account/FamilyOverview';
import MyInformation from './account/MyInformation';
import SubscriptionSettings from './account/SubscriptionSettings';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTranslation } from '../hooks/useTranslation';
import { SettingsIcon, UsersIcon, LightbulbIcon, CreditCardIcon } from '../components/common/icons';

export default function Account() {
  const { hasAccess, gateEnabled } = useSubscription();
  const { t } = useTranslation();
  const navRef = useRef(null);
  const location = useLocation();

  // While the gate is up, only settings (GDPR erasure lives there) and the
  // subscription tab stay reachable — family and minner are paid surface.
  // hasAccess is the server-computed verdict from /status, same rule as the
  // API gate (backend lib/subscriptionAccess.js) — never re-derived here.
  const gated = gateEnabled && !hasAccess;

  // A deep-linked tab (e.g. /account/abonnement from the subscription page)
  // can start off-screen in the scrollable row. 'nearest' on both axes keeps
  // the scroll inside the nav — the page itself never moves.
  useEffect(() => {
    const active = navRef.current?.querySelector('[aria-current="page"]');
    active?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
  }, [location.pathname]);

  const tabClass = ({ isActive }) =>
    `flex-shrink-0 whitespace-nowrap py-3 flex items-center border-b-2 ${isActive ? 'border-primary text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'}`;

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">Konto</h1>

      <div className="relative mb-8">
        <nav ref={navRef} className="border-b flex gap-6 overflow-x-auto scrollbar-hide pr-8 scroll-pr-8">
          <NavLink to="settings" className={tabClass} end>
            <SettingsIcon className="w-4 h-4 mr-2" />
            Innstillinger
          </NavLink>
          {!gated && (
            <NavLink to="family" className={tabClass} end>
              <UsersIcon className="w-4 h-4 mr-2" />
              Min familie
            </NavLink>
          )}
          {!gated && (
            <NavLink to="coord-minner" className={tabClass} end>
              <LightbulbIcon className="w-4 h-4 mr-2" />
              Coord minner
            </NavLink>
          )}
          <NavLink to="abonnement" className={tabClass} end>
            <CreditCardIcon className="w-4 h-4 mr-2" />
            {t('accountSubscription.tabTitle')}
          </NavLink>
        </nav>
        {/* Right-edge fade hinting at off-screen tabs. Same idiom as the
            ConversationView fade; matches the page bg so it is invisible on
            desktop where every tab fits. bottom-px leaves the nav's own
            border-b crisp under the gradient. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-0 bottom-px right-0 w-8 bg-gradient-to-l from-background to-transparent"
        />
      </div>

      <Routes>
        <Route path="settings" element={<AccountSettings />} />
        <Route path="family" element={gated ? <Navigate to="../settings" replace /> : <FamilyOverview />} />
        <Route path="coord-minner" element={gated ? <Navigate to="../settings" replace /> : <MyInformation />} />
        <Route path="abonnement" element={<SubscriptionSettings />} />
        <Route path="*" element={<Navigate to="settings" replace />} />
      </Routes>
    </div>
  );
}
